const generatePkceRandomString = (length: number): string => {
    const HEXA_RADIX = 16;
    const LAST_TWO_CHARACTERS = -2;

    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, (dec) =>
        `0${dec.toString(HEXA_RADIX)}`.slice(LAST_TWO_CHARACTERS),
    ).join('');
};

/**
 * Generate a secure random string using the browser crypto functions
 */
export const generateCodeVerifier = (): string => {
    const CODE_VERIFIER_LENGTH = 32;

    return generatePkceRandomString(CODE_VERIFIER_LENGTH);
};

/**
 * Generates nonce phrase. Used to verify that returned idToken is issued
 * for the client.
 */
export const generateNonce = (): string => {
    const NONCE_LENGTH = 16;

    return generatePkceRandomString(NONCE_LENGTH);
};

/**
 * Calculate the SHA256 hash of the input text.
 * Returns a promise that resolves to an ArrayBuffer
 */
const getHashedCodeVerifier = (codeVerifier: string): Promise<ArrayBuffer> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    return window.crypto.subtle.digest('SHA-256', data);
};

/**
 * Base64-urlencodes the input string
 */
const base64urlencode = (hashedCodeVerifier: ArrayBuffer): string => {
    // Convert the ArrayBuffer to string using Uint8 array to convert to what btoa accepts.
    // btoa accepts chars only within ascii 0-255 and base64 encodes them.
    // Then convert the base64 encoded to base64url encoded
    //   (replace + with -, replace / with _, trim trailing =)
    const hashedCoreVerifierAsArray = Array.from(new Uint8Array(hashedCodeVerifier));

    return btoa(String.fromCharCode.apply(null, hashedCoreVerifierAsArray))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

/**
 * Return the base64-urlencoded sha256 hash for the PKCE challenge
 */
export const getCodeChallengeFromCodeVerifier = async (codeVerifier: string): Promise<string> => {
    const hashedCodeVerifier = await getHashedCodeVerifier(codeVerifier);
    return base64urlencode(hashedCodeVerifier);
};
